import classNames from "classnames";
import styles from "./cell.module.scss";
import { Skeleton } from "../../skeleton";
import { Cell, CoreCellProps } from "./cell";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../Tooltip";
import { useRef } from "react";

export interface NumericalCellProps extends CoreCellProps {
  value: number | string | null | undefined;
  decimalPlaces?: number;
  prefix?: string;
  suffix?: string;
  thousandsSeparator?: string;
}

export const CellNumerical = ({
  value,
  decimalPlaces,
  prefix = "",
  suffix = "",
  thousandsSeparator,
  showTooltip = true,
  ...rest
}: NumericalCellProps) => {
  let formattedNumber;

  if (value !== null && value !== undefined) {
    const [integerPart, decimalPart = ""] = value.toString().split(".");
    const formattedInteger = thousandsSeparator
      ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)
      : integerPart;

    const truncatedDecimalPart = decimalPart.slice(0, decimalPlaces);

    formattedNumber = decimalPart
      ? `${formattedInteger}${
          decimalPlaces && decimalPlaces > 0 ? "." : ""
        }${truncatedDecimalPart}`
      : formattedInteger;
  }

  const tooltipText = `${prefix}${formattedNumber}${suffix}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const isTextOverflown =
    containerRef.current && textRef.current
      ? textRef.current.clientWidth > containerRef.current.clientWidth
      : false;

  return (
    <>
      <Cell
        alignment="right"
        skeleton={<Skeleton style={{ width: 32 }} />}
        {...rest}
      >
        {value !== null && value !== undefined && (
          <div ref={containerRef} className={styles.cellContent}>
            {/*
              This text does not get displayed!
              This element will determine if the text width is greater than the container width.
              Which will ultimately display a tooltip if so. 
            */}
            <div
              ref={textRef}
              className={styles.tooltipTriggerText}
              aria-hidden
            >
              {prefix}
              {formattedNumber}
              {suffix}
            </div>

            {!isTextOverflown || !showTooltip ? (
              <>
                {prefix}
                {formattedNumber}
                {suffix}
              </>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={styles.cellContent}>
                    {prefix}
                    {formattedNumber}
                    {suffix}
                  </div>
                </TooltipTrigger>

                <TooltipContent variant="small" align="center" side="left">
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </Cell>
    </>
  );
};
